import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, PackageType } from '@/types';
import { userService } from '@/services/storageService';
import { setAuthToken } from '@/services/apiClient';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';

export type MultiAccountAccount = {
  id: number;
  access_token: string;
  account_type?: string;
  role?: string;
  first_name?: string;
  last_name?: string;
};

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  token: string | null;

  login: (
    email: string,
    password: string
  ) => Promise<{
    success: boolean;
    error?: string;
    needsFirstLogin?: boolean;
    needsPasswordReset?: boolean;
    requires2fa?: boolean;
    otpSessionId?: number;
    multiAccount?: boolean;
    accounts?: MultiAccountAccount[];
    token?: string;
    role?: User['role'];
    user?: User;
  }>;

  // バ. UPDATED: supports OTP
  signup: (data: SignupInput) => Promise<{ success: boolean; error?: string; token?: string }>;

  logout: () => void;
  completeFirstLogin: (
    token: string,
    newPassword: string,
    acceptPrivacyPolicy?: boolean,
    acceptTermsAndConditions?: boolean
  ) => Promise<{ success: boolean; error?: string }>;
  verifyLoginOtp: (
    otpSessionId: number,
    otp: string
  ) => Promise<{
    success: boolean;
    error?: string;
    role?: User['role'];
    user?: User;
  }>;
  updateUser: (updates: Partial<User>) => void;
  setSessionFromToken: (token: string) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = 'bcm_auth_token';
const SESSION_IDLE_MINUTES = 30;
const SESSION_IDLE_TIMEOUT_MS = SESSION_IDLE_MINUTES * 60 * 1000;

const readStoredToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY);
const writeStoredToken = (value: string | null) => {
  if (value) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, value);
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }
};

type TokenResponse = {
  access_token?: string;
  first_time_token?: string;
  needs_first_login?: boolean;
  needs_password_reset?: boolean;
  reset_token?: string;
  requires_2fa?: boolean;
  otp_session_id?: number;
};

type RawEnum = string | { value?: string } | undefined | null;

interface ApiUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role?: RawEnum;
  package?: RawEnum;
  nickname?: string | null;
  is_super_admin?: boolean;
  needs_consent?: boolean;
  two_factor_enabled?: boolean;
}

const normalizeEnumValue = (value: RawEnum) => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    return value.includes('.') ? value.split('.').pop() : value;
  }
  if (typeof value === 'object' && value && 'value' in value) {
    return value.value;
  }
  return value as any;
};

const mapUser = (apiUser: ApiUser): User => {
  const normalizedRole = normalizeEnumValue(apiUser.role)?.toString().toUpperCase();

  let role: User['role'] = 'member';
  if (apiUser.is_super_admin) role = 'admin';
  else if (normalizedRole === 'ADMIN') role = 'admin';
  else if (normalizedRole === 'COACH') role = 'coach';

  return {
    id: String(apiUser.id),
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    role,
    packageType: normalizeEnumValue(apiUser.package)?.toString().toLowerCase() as PackageType | undefined,
    nickname: apiUser.nickname ?? undefined,
    isSuperAdmin: Boolean(apiUser.is_super_admin),
    needsConsent: Boolean(apiUser.needs_consent),
    twoFactorEnabled: Boolean(apiUser.two_factor_enabled),
  };
};

// バ. NEW: SignupInput supports both old and new callers
export type SignupInput = {
  email: string;
  password: string;

  // allow both (backward compatible)
  firstName?: string;
  lastName?: string;
  packageType?: PackageType;

  // preferred new keys (snake_case)
  first_name?: string;
  last_name?: string;
  package?: string;
  club_name?: string;
  sport_type?: string;
  is_yearly_plan?: boolean;
  yearly_discount_pct?: number;
  yearly_price_at_signup?: number;

  // バ. OTP enforcement
  otp_session_id?: number;
  accept_privacy_policy?: boolean;
  accept_data_collection?: boolean;
  accept_terms_and_conditions?: boolean;
};

function extractFastApiMessage(err: unknown): string {
  // userService may throw Error(message) already, but this makes it safer
  if (err instanceof Error) return err.message;
  return String(err);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setTokenState] = useState<string | null>(null);
  const navigate = useNavigate();

  const performLogout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    writeStoredToken(null);
    setTokenState(null);
  }, []);

  const handleAutoLogout = useCallback(() => {
    performLogout();
    toast.info(t('auth_logged_out_inactivity', 'You were logged out due to inactivity.'));
    navigate('/login', { replace: true });
  }, [navigate, performLogout, t]);

  const idleTimer = useIdleTimer({
    timeout: SESSION_IDLE_TIMEOUT_MS,
    onIdle: handleAutoLogout,
    startManually: true,
    crossTab: true,
    name: 'bcm-auth-idle-timer',
    syncTimers: 60_000,
    eventsThrottle: 500,
    debounce: 250,
    disabled: !user,
  });

  useEffect(() => {
    (async () => {
      try {
        const storedToken = readStoredToken();
        if (!storedToken) {
          setIsLoading(false);
          return;
        }

        const me = (await userService.me(storedToken)) as ApiUser;
        if (me) {
          setAuthToken(storedToken);
          setUser(mapUser(me));
          setTokenState(storedToken);
        }
      } catch (err) {
        performLogout();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [performLogout]);

  useEffect(() => {
    if (!user) {
      idleTimer.reset();
      idleTimer.pause();
      return;
    }

    idleTimer.reset();
    idleTimer.start();
  }, [idleTimer, user]);

  const setSessionFromToken = async (token: string) => {
    try {
      if (!token) return null;
      setAuthToken(token);
      writeStoredToken(token);
      setTokenState(token);
      const me = (await userService.me(token)) as ApiUser;
      const mapped = mapUser(me);
      setUser(mapped);
      return mapped;
    } catch (err) {
      performLogout();
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const res = (await userService.login(email, password)) as TokenResponse & {
        needsFirstLogin?: boolean;
        needsPasswordReset?: boolean;
        firstTimeToken?: string;
        resetToken?: string;
      };

      const token = res?.access_token;
      const firstLoginToken = res?.first_time_token || (res as any)?.firstTimeToken;
      const needsFirstLogin = Boolean(res?.needs_first_login ?? (res as any)?.needsFirstLogin ?? firstLoginToken);
      const needsPasswordReset = Boolean(res?.needs_password_reset ?? (res as any)?.needsPasswordReset);
      const resetToken = res?.reset_token ?? (res as any)?.resetToken;
      const requires2fa = Boolean(res?.requires_2fa);
      const otpSessionId = typeof res?.otp_session_id === 'number' ? res.otp_session_id : undefined;

      if (needsFirstLogin) {
        if (firstLoginToken) {
          return { success: false, needsFirstLogin: true, token: firstLoginToken };
        }
        return { success: false, error: 'First-time login required. Token missing.' };
      }

      if (needsPasswordReset) {
        if (resetToken) {
          return { success: false, needsPasswordReset: true, token: resetToken };
        }
        return { success: false, needsPasswordReset: true, error: 'Password reset required. Check your email.' };
      }

      if (requires2fa) {
        if (otpSessionId) {
          return { success: false, requires2fa: true, otpSessionId };
        }
        return { success: false, error: '2FA verification is required but OTP session is missing.' };
      }

      if (!token) return { success: false, error: 'No token returned' };

      const mapped = await setSessionFromToken(token);
      if (!mapped) return { success: false, error: 'Unable to load account' };

      return { success: true, role: mapped.role, user: mapped };
    } catch (err: unknown) {
      return { success: false, error: extractFastApiMessage(err) };
    }
  };

  // バ. UPDATED signup: converts to backend schema keys, but does NOT auto-login (user must log in after signup)
  const signup = async (data: SignupInput) => {
    try {
      const payload = {
        email: data.email,
        password: data.password,

        // バ. backend expects snake_case
        first_name: data.first_name ?? data.firstName ?? '',
        last_name: data.last_name ?? data.lastName ?? '',

        // backend expects "package" (string) like "package1"
        package: data.package ?? data.packageType,
        club_name: data.club_name,
        sport_type: data.sport_type,
        is_yearly_plan: Boolean(data.is_yearly_plan),
        yearly_discount_pct: data.yearly_discount_pct,
        yearly_price_at_signup: data.yearly_price_at_signup,

        // バ. REQUIRED by your backend SignupSchema
        otp_session_id: data.otp_session_id,
        accept_privacy_policy: Boolean(data.accept_privacy_policy),
        accept_data_collection: Boolean(data.accept_terms_and_conditions ?? data.accept_data_collection),
        accept_terms_and_conditions: Boolean(data.accept_terms_and_conditions ?? data.accept_data_collection),
      };

      if (!payload.first_name || !payload.last_name) {
        return { success: false, error: 'First name and last name are required' };
      }
      if (!payload.otp_session_id) {
        return { success: false, error: 'Email OTP verification required' };
      }

      const res = (await userService.signup(payload as any)) as { access_token?: string };

      // Do not set token/user here; force explicit login after signup
      return { success: true, requiresLogin: true, token: res?.access_token };
    } catch (err: unknown) {
      return { success: false, error: extractFastApiMessage(err) };
    }
  };

  const completeFirstLogin = async (
    token: string,
    newPassword: string,
    acceptPrivacyPolicy = true,
    acceptTermsAndConditions = true,
  ) => {
    try {
      await userService.firstTimeLogin(
        token,
        newPassword,
        undefined,
        acceptPrivacyPolicy,
        acceptTermsAndConditions,
      );
      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: extractFastApiMessage(err) };
    }
  };

  const verifyLoginOtp = async (otpSessionId: number, otp: string) => {
    try {
      const res = await userService.verifyLoginOtp({ otp_session_id: otpSessionId, otp });
      const token = res?.access_token;
      if (!token) {
        return { success: false, error: 'No token returned after OTP verification' };
      }
      const mapped = await setSessionFromToken(token);
      if (!mapped) return { success: false, error: 'Unable to load account' };
      return { success: true, role: mapped.role, user: mapped };
    } catch (err: unknown) {
      return { success: false, error: extractFastApiMessage(err) };
    }
  };

  const logout = () => {
    performLogout();
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return;
    setUser({ ...user, ...updates });
  };

  return (
    <AuthContext.Provider
      value={{ user, isLoading, token, login, signup, logout, completeFirstLogin, verifyLoginOtp, updateUser, setSessionFromToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
